import {
  badgeRecipe,
  buttonRecipe,
  cardRecipe,
  designTokens,
  inputRecipe,
  tableRecipe,
} from "@construct/design-system";

type TokenPath = string;
type CssVarValue = string | number;

const colorVars: Record<TokenPath, string> = {
  "--color-page": designTokens.colors.page,
  "--color-surface": designTokens.colors.surface,
  "--color-secondary-surface": designTokens.colors.secondarySurface,
  "--color-header": designTokens.colors.header,
  "--color-header-border": designTokens.colors.headerBorder,
  "--color-footer": designTokens.colors.footer,
  "--color-primary-text": designTokens.colors.primaryText,
  "--color-secondary-text": designTokens.colors.secondaryText,
  "--color-placeholder-text": designTokens.colors.placeholderText,
  "--color-border": designTokens.colors.border,
  "--color-focus-ring": designTokens.colors.focusRing,
  "--color-brand": designTokens.colors.brand,
  "--color-hover": designTokens.colors.hover,
  "--color-primary-button": designTokens.colors.primaryButton,
  "--color-primary-button-text": designTokens.colors.primaryButtonText,
  "--color-secondary-button": designTokens.colors.secondaryButton,
  "--color-secondary-button-text": designTokens.colors.secondaryButtonText,
  "--color-selected-button": designTokens.colors.selectedButton,
  "--color-selected-button-text": designTokens.colors.selectedButtonText,
  "--color-danger": designTokens.rawColors.semantic.danger.fg,
  "--color-danger-button": designTokens.colors.danger,
  "--color-danger-soft": designTokens.colors.dangerSoft,
  "--color-success": designTokens.colors.success,
  "--color-success-soft": designTokens.colors.successSoft,
  "--color-warning": designTokens.colors.warning,
  "--color-warning-soft": designTokens.colors.warningSoft,
  "--color-info": designTokens.colors.info,
  "--color-info-soft": designTokens.colors.infoSoft,
  "--color-divider": designTokens.rawColors.border.divider,
  "--color-brand-surface": designTokens.rawColors.brand.surface,
  "--color-brand-border": designTokens.rawColors.brand.border,
  "--color-search-active": designTokens.rawColors.background.searchActive,
};

const unitlessVars: Record<TokenPath, CssVarValue> = {
  "--font-body-weight": designTokens.typography.bodyWeight,
  "--font-heading-weight": designTokens.typography.headingWeight,
  "--line-height": designTokens.typography.lineHeight,
};

const lengthVars: Record<TokenPath, CssVarValue> = {
  "--radius-xs": designTokens.radii.raw.xs,
  "--radius-sm": designTokens.radii.raw.sm,
  "--radius-md": designTokens.radii.raw.md,
  "--radius-lg": designTokens.radii.raw.lg,
  "--radius-card": designTokens.radii.card,
  "--radius-button": designTokens.radii.button,
  "--radius-input": designTokens.radii.input,
  "--radius-badge": designTokens.radii.badge,
  "--input-active-height": inputRecipe.active.minHeight,
  "--card-padding": cardRecipe.default.padding,
  "--badge-padding-x": badgeRecipe.info.paddingInline,
  "--badge-padding-y": badgeRecipe.info.paddingBlock,
};

const recipeVars: Record<TokenPath, CssVarValue> = {
  "--font-product": designTokens.typography.fontFamily,
  "--font-documentation": designTokens.typography.raw.documentation.family,
  "--font-mono": designTokens.typography.raw.mono.family,
  "--border-width": designTokens.borders.defaultWidth,
  "--focus-ring-width": designTokens.borders.focusRingWidth,
  "--shadow-button": designTokens.shadows.button,
  "--shadow-popover": designTokens.shadows.popover,
  "--motion-hover": designTokens.motion.hover,
  "--motion-base": designTokens.motion.base,
  "--button-primary-hover": String(buttonRecipe.primary.hoverBackground),
  "--button-primary-active": String(buttonRecipe.primary.activeBackground),
  "--button-secondary-hover-border": String(buttonRecipe.secondary.hoverBorderColor),
  "--button-secondary-active-bg": String(buttonRecipe.secondary.activeBackground),
  "--button-secondary-active-border": String(buttonRecipe.secondary.activeBorderColor),
  "--table-header-bg": String(tableRecipe.header.background),
  "--table-row-hover-bg": String(tableRecipe.row.hoverBackground),
};

const spacingVars = designTokens.spacing.scale.reduce<Record<string, number>>((vars, value, index) => {
  vars[`--space-${index}`] = value;
  return vars;
}, {});

export function installThemeVars(): void {
  const root = document.documentElement;
  const vars = { ...colorVars, ...recipeVars, ...unitlessVars };

  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, String(value));
  }

  for (const [name, value] of Object.entries({ ...lengthVars, ...spacingVars })) {
    root.style.setProperty(name, typeof value === "number" ? `${value}px` : value);
  }
}
